// @owners { team: patient-health }
import { COLORS } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  Body,
  Button,
  Card,
  Column,
  InlineAlert,
  LgPadding,
  Link,
  LoadingEmptyState,
  MdSpacing,
  Row,
  SecondaryPage,
  XlPadding,
  XlSpacing,
  XsSpacing,
} from '@alto/design-system';
import { useQueryInsuranceConfigs } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React, { useContext } from 'react';
import { push } from 'react-router-redux';
// eslint-disable-next-line import/no-deprecated
import { deleteInsurance } from '~shared/actions/insurances';
import { setEditingInsuranceID } from '~shared/actions/onboarding';
import { getDeliveries } from '~shared/features/delivery/selectors/getDeliveries';
import getIsPhotoUpload from '~shared/features/insurances/helpers/getIsPhotoUpload';
import { getInsurances } from '~shared/features/insurances/selectors/getInsurances';
import {
  INSURANCE_FOUND,
  INSURANCE_INPUT,
  INSURANCE_INPUT_MANUAL,
  INSURANCE_INPUT_PHOTO,
} from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import { routeFromPath } from '~shared/features/onboarding/helpers/routeToPath';
import getErrors from '~shared/features/ui/selectors/getErrors';
import getLoading from '~shared/features/ui/selectors/getLoading';
import getShowProgynySelfServiceForUser from '~shared/features/users/selectors/getShowProgynySelfServiceForUser';
import { useAnalytics } from '~shared/hooks/useAnalytics';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { getDeliveriesBilledWithInsuranceCount } from '~shared/lib/analytics/src/getDeliveriesBilledWithInsuranceCount';
import getInsuranceSource from '~shared/lib/analytics/src/getInsuranceSource';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type Insurance, type Insurance as InsuranceType } from '~shared/types/clients';
import { FAQs } from '~web/features/onboarding/components/redesign/FAQs';
import { InsuranceDetails } from '~web/features/onboarding/components/redesign/InsuranceDetails';
import { OnboardingHeader } from '~web/features/onboarding/components/redesign/OnboardingHeader';
import { OnboardingNavBarLogo } from '~web/features/onboarding/components/redesign/OnboardingNavBarLogo';
import {
  nextUrl as getNextUrl,
  previousUrl as getOnboardingPreviousUrl,
} from '~web/features/onboarding/helpers/urlHandler';

export const OnboardingInsuranceFoundPage = () => {
  const dispatch = useDispatchShared();
  const { trackEvent } = useAnalytics();
  const { getCurrentRouteName } = useNavigation();
  const { closeActionSheet, setActiveActionSheet } = useContext(ActionSheetContext);
  const showProgyny = useSelectorShared(getShowProgynySelfServiceForUser);
  const deliveries = useSelectorShared(getDeliveries);
  const source = useSelectorShared(getInsuranceSource);
  const insurances = useSelectorShared(getInsurances);
  const { fetchInsurancesLoading } = useSelectorShared(getLoading);
  const { fetchInsurancesError } = useSelectorShared(getErrors);
  const loading = fetchInsurancesLoading && !insurances.length;
  const photoUrl = useSelectorShared((state) => getNextUrl(state, INSURANCE_FOUND, INSURANCE_INPUT_PHOTO));
  const manualUrl = useSelectorShared((state) => getNextUrl(state, INSURANCE_FOUND, INSURANCE_INPUT_MANUAL));
  const addInsuranceUrl = useSelectorShared((state) => getNextUrl(state, INSURANCE_FOUND, INSURANCE_INPUT));
  const nextUrl = useSelectorShared((state) => getNextUrl(state, INSURANCE_FOUND));
  const routeName = getCurrentRouteName() || '';
  const currentRoute = routeFromPath(routeName);
  const onboardingPreviousUrl = useSelectorShared((state) => getOnboardingPreviousUrl(state, currentRoute));

  const titleText = showProgyny ? 'Along with your Progyny benefits, we found your additional insurance!' : 'Benefits';
  const subtitleText = showProgyny
    ? 'This is separate from your Progyny benefits, and helps us find you a great price on any medications not covered by Progyny.'
    : "Review the info below to make sure it's correct. Have additional insurance or prescription savings cards? Add them all so we can get you the best pricing.";

  const { headerBackgroundColor, headerLogoUrl, isFetching } = useQueryInsuranceConfigs();

  const handleDeleteInsurance = (insurance: Insurance) => {
    // eslint-disable-next-line import/no-deprecated
    dispatch(deleteInsurance(insurance));
    const billedDeliveriesCount = getDeliveriesBilledWithInsuranceCount(deliveries, insurance.id);
    trackEvent({
      event: EVENTS.INSURANCE_REMOVED,
      params: {
        source,
        'insurance source': insurance.source,
        'num deliveries billed with insurance': billedDeliveriesCount,
      },
    });
  };

  const handleEditInsurance = (insurance: InsuranceType) => {
    const url = insurance.image_url ? photoUrl : manualUrl;
    dispatch(setEditingInsuranceID(insurance.id));

    dispatch(push(url));
  };

  const handleAddInsuranceClick = () => {
    dispatch(push(addInsuranceUrl));
  };

  const handleConfirm = () => {
    dispatch(push(nextUrl));
  };

  const handleBack = () => {
    if (onboardingPreviousUrl) {
      dispatch(push(onboardingPreviousUrl));
    }
  };

  const handleDelete = (insuranceItem: Insurance) => {
    setActiveActionSheet(
      <ActionSheetV2
        title="Confirm deletion"
        description="Are you sure you want to delete this benefit?"
        handleClose={closeActionSheet}
        buttons={[
          <Button
            key="delete"
            type="primary"
            variant="danger"
            onPress={() => {
              handleDeleteInsurance(insuranceItem);
              closeActionSheet();
            }}
          >
            Delete
          </Button>,
          <Button
            key="cancel"
            type="tertiary"
            onPress={closeActionSheet}
          >
            Cancel
          </Button>,
        ]}
      />,
    );
  };

  if (isFetching) {
    return (
      <XlPadding>
        <LoadingEmptyState />
      </XlPadding>
    );
  }

  return (
    <SecondaryPage
      withoutWebFramingElementHeights
      buttons={
        !fetchInsurancesError && !loading && !isFetching
          ? [
              <Button
                type="tertiary"
                key="confirm"
                label="Next"
                onPress={handleConfirm}
              />,
              <Button
                type="primary"
                key="confirm"
                label="Add another benefit"
                onPress={handleAddInsuranceClick}
              />,
            ]
          : []
      }
      footerPlacementContext="webScreenWithoutPolicyFooter"
      onDismiss={handleBack}
      dismissIcon="chevronleft"
      headerBackgroundColor={headerBackgroundColor}
      HeaderContent={
        <OnboardingHeader
          title={titleText}
          subtitle={<Body center>{subtitleText}</Body>}
        />
      }
      NavBarCenterContent={<OnboardingNavBarLogo illustrationSrc={headerLogoUrl} />}
      NavBarRightPressable={<FAQs />}
    >
      {loading ? <LoadingEmptyState /> : null}
      {fetchInsurancesError && !loading ? (
        <>
          <InlineAlert type="error">
            <Body>Looks like we're having some technical difficulty. Please refresh this page.</Body>
          </InlineAlert>
          <XlSpacing />
        </>
      ) : null}
      {!loading && !fetchInsurancesError && (
        <Column>
          {insurances.map((insuranceItem, index) => (
            <>
              <Card key={insuranceItem.id}>
                <LgPadding>
                  <Column>
                    <InsuranceDetails insurance={insuranceItem} />
                    {insuranceItem.source !== 'cardfinder' && (
                      <>
                        <XsSpacing key="edit insurance spacing" />
                        <Row centerVertically>
                          <Link
                            textSize="mini"
                            onPress={() => {
                              handleEditInsurance(insuranceItem);
                            }}
                            accessibilityLabel="edit insurance"
                          >
                            {getIsPhotoUpload(insuranceItem) ? 'VIEW' : 'EDIT'}
                          </Link>
                          <Body color={COLORS.PALETTE.SECONDARY.DARK}>{' | '}</Body>
                          <Link
                            textSize="mini"
                            onPress={() => {
                              handleDelete(insuranceItem);
                            }}
                            accessibilityLabel="delete insurance"
                          >
                            DELETE
                          </Link>
                        </Row>
                      </>
                    )}
                  </Column>
                </LgPadding>
              </Card>
              {index !== insurances.length - 1 && <MdSpacing />}
            </>
          ))}
        </Column>
      )}
    </SecondaryPage>
  );
};
